import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import DownloadIcon from "@mui/icons-material/CloudDownload";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import {
  ExportBatchInfo,
  useGetCompanyExportBatchesQuery,
  useStartCertificateExportMutation,
} from "@/apis/certificatesApi";
import { AudienceSelector } from "@/components/AudienceSelector";
import { Spinner } from "@/components/Spinner";
import { CourseSelector } from "@/components/courseSelector";
import {
  Audience,
  CourseSelectionMode,
  UserData,
  selectCertificateExport,
  setCourseSelectionMode,
  setSelectedCourses,
  setSelectedDepartments,
  setSelectedUsers,
  setTargetedAudience,
} from "@/pages/awareness/progress/certificate_export/CertificateExportSlice";
import { useSimulationResource } from "@/utils/ResourceGet";
import { HeaderBox } from "@/utils/styled-components";

const CertificateExportTable: React.FC = () => {
  const { t } = useTranslation();
  const [shouldPollBatches, setShouldPollBatches] = useState(false);
  const dispatch = useDispatch();
  const [userList] = useSimulationResource("users/basic_infos", []);
  const certificateExport = useSelector(selectCertificateExport);
  const [startExport] = useStartCertificateExportMutation();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const {
    data: batches,
    isLoading,
    isError,
  } = useGetCompanyExportBatchesQuery(null, {
    pollingInterval: shouldPollBatches ? 2000 : 0,
    skipPollingIfUnfocused: true,
  });

  useEffect(() => {
    if (
      batches &&
      batches.info &&
      !batches.info.some(
        (batchinfo: ExportBatchInfo) => batchinfo.status === "to_do",
      )
    ) {
      setShouldPollBatches(false);
    }
  }, [batches]);

  const columns: GridColDef<ExportBatchInfo>[] = [
    {
      field: "export_id",
      valueGetter: (_, row) => {
        return row.export_id.value;
      },
      headerName: t("Export Id"),
    },
    {
      field: "fileName",
      valueGetter: (_, row) => {
        if (row.archive_location === null) {
          return t("No archive");
        } else {
          return row.archive_location.name.split("/")[1];
        }
      },
      headerName: t("File Name"),
      width: 200,
    },
    {
      field: "link",
      valueGetter: (_, row) => {
        if (row.archive_link === null) {
          return t("No link");
        } else {
          return row.archive_link.url;
        }
      },
      headerName: t("Link"),
      width: 300,
      renderCell: (params) => {
        if (params.value === t("No link")) {
          return <></>;
        }
        return (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            {t("Download")}
          </a>
        );
      },
    },
    {
      field: "status",
      display: "flex",
      renderCell: (params) => {
        switch (params.value) {
          case "to_do":
            return (
              <Typography>
                <Trans i18nKey="CertificateExportInProgress">
                  {"In Progress"}
                </Trans>
                <LinearProgress />
              </Typography>
            );
          case "done":
            return (
              <Typography>
                <Trans i18nKey="CertificateExportDone">{"Done"}</Trans>
              </Typography>
            );
          case "failed":
            return (
              <Typography>
                <Trans i18nKey="CertificateExportFailed">{"Failed"}</Trans>
              </Typography>
            );
        }
      },
      headerName: t("Status"),
      width: 150,
    },
    { field: "export_date", headerName: t("Export Date") },
  ];

  const startCertificateExport = () => {
    const payload = {
      audienceType: certificateExport.targetedAudience,
      usersToExportAdresses: certificateExport.selectedUsers.map(
        (selectedUser: UserData) => selectedUser.email,
      ),
      departementsToExportIds: certificateExport.selectedDepartments.map(
        (selectedDepartment) => selectedDepartment.id,
      ),
      course_selection_mode: certificateExport.courseSelectionMode,
      selected_course_ids: certificateExport.selectedCourses.map(
        (course) => course.id,
      ),
    };
    startExport(payload);
    setShouldPollBatches(true);
  };

  return (
    <>
      <Card>
        <CardContent>
          <CardHeader
            action={
              <HeaderBox>
                <Button
                  variant={"outlined"}
                  size={"small"}
                  sx={{ marginRight: "5px" }}
                  startIcon={<DownloadIcon />}
                  onClick={() => setSelectorOpen(true)}
                >
                  {t("Download Certificates")}
                </Button>
              </HeaderBox>
            }
          />
          {isLoading ? (
            <Spinner />
          ) : (
            !isError && (
              <div style={{ height: "75vh", width: "100%" }}>
                <DataGrid
                  rows={batches.info}
                  columns={columns}
                  getRowId={(row) => {
                    return row.export_id.value + row.sequence_number;
                  }}
                />
              </div>
            )
          )}
        </CardContent>
      </Card>
      <Dialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        fullWidth
      >
        <DialogTitle>{t("Who needs to be certified ?")}</DialogTitle>
        <DialogContent>
          <AudienceSelector
            audienceSelectionMode={certificateExport.targetedAudience}
            setAudienceSelectionMode={(mode: Audience) =>
              dispatch(setTargetedAudience(mode))
            }
            setSelectedDepartments={(deps) => {
              dispatch(setSelectedDepartments(deps));
            }}
            selectedUsers={certificateExport.selectedUsers}
            setSelectedUsers={(users) => {
              dispatch(setSelectedUsers(users));
            }}
            fetchUsers={() => {
              return;
            }}
            selectedDepartments={certificateExport.selectedDepartments}
            userList={userList}
          />
        </DialogContent>
        <DialogTitle>{t("What courses should be included ?")}</DialogTitle>
        <DialogContent>
          <CourseSelector
            selectionMode={certificateExport.courseSelectionMode}
            onSelectionModeChange={(mode: CourseSelectionMode) =>
              dispatch(setCourseSelectionMode(mode))
            }
            selectedCourses={certificateExport.selectedCourses}
            onSelectedCoursesChange={(courses) => {
              dispatch(setSelectedCourses(courses));
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              startCertificateExport();
              setSelectorOpen(false);
            }}
          >
            {t("Start Export")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CertificateExportTable;
